import { useEffect, useRef, useState } from "react";
import { Share2, Link2, Check } from "lucide-react";
import { api } from "../../lib/api";

export const QUIZ_COMPLETION_KEY = "clinq_quiz_completion_v1";

export function readQuizCompletion() {
  try { return JSON.parse(localStorage.getItem(QUIZ_COMPLETION_KEY) || "null"); } catch { return null; }
}

// Links an anonymous quiz completion to the founder who just signed up / logged in.
export async function convertQuizCompletion(apiClient) {
  const c = readQuizCompletion();
  if (!c?.completion_id) return;
  try { await apiClient.post(`/founder/quiz-completions/${c.completion_id}/convert`); } catch { /* best-effort */ }
  try { localStorage.removeItem(QUIZ_COMPLETION_KEY); } catch { /* ignore */ }
}

const WA_ICON = (
  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
    <path d="M17.5 14.4c-.3-.1-1.8-.9-2-1-.3-.1-.5-.1-.7.1-.2.3-.8 1-.9 1.2-.2.2-.3.2-.6.1-.3-.1-1.3-.5-2.4-1.5-.9-.8-1.5-1.8-1.7-2.1-.2-.3 0-.5.1-.6l.5-.6c.1-.2.2-.3.3-.5.1-.2 0-.4 0-.5l-.9-2.2c-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.1.2 2.1 3.2 5.1 4.5.7.3 1.3.5 1.7.6.7.2 1.4.2 1.9.1.6-.1 1.8-.7 2-1.4.2-.7.2-1.3.2-1.4-.1-.2-.3-.2-.6-.3zM12 2a10 10 0 0 0-8.6 15.1L2 22l5-1.3A10 10 0 1 0 12 2zm0 18.2c-1.5 0-3-.4-4.3-1.2l-.3-.2-3 .8.8-2.9-.2-.3A8.2 8.2 0 1 1 12 20.2z" />
  </svg>
);
const LI_ICON = (
  <svg viewBox="0 0 24 24" className="h-4 w-4" fill="currentColor" aria-hidden="true">
    <path d="M20.4 2H3.6A1.6 1.6 0 0 0 2 3.6v16.8A1.6 1.6 0 0 0 3.6 22h16.8a1.6 1.6 0 0 0 1.6-1.6V3.6A1.6 1.6 0 0 0 20.4 2zM8 19H5V9h3v10zM6.5 7.7a1.7 1.7 0 1 1 0-3.4 1.7 1.7 0 0 1 0 3.4zM19 19h-3v-4.9c0-1.2 0-2.7-1.6-2.7s-1.9 1.3-1.9 2.6V19h-3V9h2.9v1.4a3.2 3.2 0 0 1 2.9-1.6c3.1 0 3.7 2 3.7 4.7V19z" />
  </svg>
);

// "Share my score" — logs the anonymous completion once, then offers
// WhatsApp / LinkedIn / copy-link via the public OG share page.
export function ShareScore({ quiz, score, bandLabel, answers }) {
  const [completionId, setCompletionId] = useState(null);
  const [copied, setCopied] = useState(false);
  const answersRef = useRef(answers);
  answersRef.current = answers;

  // One completion per (quiz, score); stale responses are discarded so the
  // share card always publishes the number shown on the ring.
  useEffect(() => {
    let cancelled = false;
    setCompletionId(null);
    const cached = readQuizCompletion();
    if (cached?.quiz === quiz && cached.score === score && cached.completion_id) { setCompletionId(cached.completion_id); return undefined; }
    let vid = null;
    try { vid = localStorage.getItem("clinq_visitor_id"); } catch { /* ignore */ }
    api.post("/public/quiz-completions", { quiz, score, answers: answersRef.current, visitor_id: vid })
      .then((r) => {
        const id = r.data?.completion_id;
        if (!id || cancelled) return;
        setCompletionId(id);
        try { localStorage.setItem(QUIZ_COMPLETION_KEY, JSON.stringify({ quiz, score, completion_id: id, at: Date.now() })); } catch { /* ignore */ }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [quiz, score]);

  const shareUrl = completionId ? `${process.env.REACT_APP_BACKEND_URL}/api/public/share/score/${completionId}` : null;
  const quizName = quiz === "project" ? "Project Readiness Quiz" : "Capital Readiness Quiz";
  const text = `I scored ${score}/100 (${bandLabel}) on Clinq's ${quizName}. See where your ${quiz === "project" ? "project" : "business"} stands with funders — free 2-minute quiz:`;

  const mark = (channel) => { if (completionId) api.post(`/public/quiz-completions/${completionId}/shared`, { channel }).catch(() => {}); };
  const openWhatsApp = () => { mark("whatsapp"); window.open(`https://wa.me/?text=${encodeURIComponent(`${text} ${shareUrl}`)}`, "_blank", "noopener"); };
  const openLinkedIn = () => { mark("linkedin"); window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, "_blank", "noopener"); };
  const copy = async () => {
    mark("copy");
    const payload = `${text} ${shareUrl}`;
    try {
      await navigator.clipboard.writeText(payload);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = payload; ta.setAttribute("readonly", ""); ta.style.position = "fixed"; ta.style.opacity = "0";
      document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); } catch { /* ignore */ }
      document.body.removeChild(ta);
    }
    setCopied(true); setTimeout(() => setCopied(false), 2000);
  };

  if (!shareUrl) return null;
  return (
    <div className="mt-6 flex flex-col items-center gap-2.5" data-testid="share-score">
      <p className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-white/50">
        <Share2 className="h-3.5 w-3.5" /> Share my score
      </p>
      <div className="flex flex-wrap items-center justify-center gap-2">
        <button type="button" data-testid="share-score-whatsapp" onClick={openWhatsApp}
          className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-400/10 px-4 py-2 text-xs font-semibold text-emerald-200 transition-colors hover:bg-emerald-400/20">
          {WA_ICON} WhatsApp
        </button>
        <button type="button" data-testid="share-score-linkedin" onClick={openLinkedIn}
          className="inline-flex items-center gap-2 rounded-full border border-sky-400/30 bg-sky-400/10 px-4 py-2 text-xs font-semibold text-sky-200 transition-colors hover:bg-sky-400/20">
          {LI_ICON} LinkedIn
        </button>
        <button type="button" data-testid="share-score-copy" onClick={copy}
          className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/[0.04] px-4 py-2 text-xs font-semibold text-white/70 transition-colors hover:bg-white/10">
          {copied ? <Check className="h-4 w-4 text-emerald-300" /> : <Link2 className="h-4 w-4" />} {copied ? "Copied" : "Copy link"}
        </button>
      </div>
    </div>
  );
}
